import { isBefore } from "date-fns";
import { usePostHog } from "posthog-js/react";
import type React from "react";

import type { SurveyDetailsV2Dto } from "@/api/types";
import { AIButton } from "@/components/Button/AIButton";
import { Button } from "@/components/Button/Button";
import { CheckIcon } from "@/components/Icons/CheckIcon";
import { Label } from "@/components/Label/Label";
import { useBool } from "@/hooks/useBool";
import { usePermission } from "@/hooks/usePermission";
import { useSlug } from "@/hooks/useSlug";
import { useConfig } from "@/providers/config/useConfig";
import { surveyMutations } from "@/queries/surveys";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import { SurveyFollowUpTipsModal } from "./SurveyFollowUpTipsModal";
import { SurveyFollowUpTipsModal_LEGACY } from "./SurveyFollowUpTipsModal_LEGACY";

enum SurveyFollowUpAction {
  CREATE_EVENT = "create_event",
  CREATE_MESSAGE = "create_message",
  CREATE_SURVEY = "create_survey",
  SEE_TIPS = "see_tips",
  IGNORE = "ignore",
}

interface SurveyActionSectionProps {
  survey: SurveyDetailsV2Dto;
}

export function SurveyActionSection({ survey }: SurveyActionSectionProps): React.ReactNode {
  const [isFollowUpModalOpened, followUpModalHandler] = useBool();

  const hasPermission = usePermission();
  const surveyAiFollowUpActions = useConfig("surveyAiFollowUpSuggestions");
  const { t } = useTranslation();
  const slug = useSlug();
  const addFollowUpDateMutation = surveyMutations.useAddFollowUpDate();
  const postHog = usePostHog();

  const handleClickFollowUpAction = (action: SurveyFollowUpAction) => {
    if (!survey.followedUpAt) {
      void addFollowUpDateMutation.mutateAsync({ surveyId: survey.id });
    }

    postHog.capture("clicked_survey_follow_up_action", {
      surveyId: survey.id,
      action,
      is_first_time: !survey.followedUpAt,
    });
  };

  const handleOpenTips = () => {
    followUpModalHandler.setTrue();

    handleClickFollowUpAction(SurveyFollowUpAction.SEE_TIPS);
  };

  const hasAiResults =
    surveyAiFollowUpActions &&
    survey.endsAt &&
    isBefore("2025-01-01T00:00:00.000Z", survey.endsAt) &&
    survey.canViewResults;

  return (
    <>
      <div className="flex w-full flex-col gap-2 rounded-lg bg-white p-4">
        {survey.followedUpAt && (
          <Label icon={<CheckIcon />} theme="green">
            {t("page.survye-details.follow-up-actions.label.followed-up")}
          </Label>
        )}
        <div className="flex w-full flex-col gap-4">
          <h3 className="text-headline3">{t("page.survey-details.follow-up-actions.title")}</h3>
          <p>{t("page.survey-details.follow-up-actions.description")}</p>
          <div className="flex w-full flex-col gap-4">
            <div className="grid w-full grid-cols-[repeat(2,1fr)] grid-rows-[repeat(2,1fr)] gap-4">
              {hasAiResults ? (
                <AIButton className="w-full" onClick={handleOpenTips}>
                  {t("page.survey-details.follow-up-actions.button.see-tips")}
                </AIButton>
              ) : (
                <Button styling="secondary" className="w-full" onClick={handleOpenTips}>
                  {t("page.survey-details.follow-up-actions.button.see-tips")}
                </Button>
              )}
              {hasPermission((x) => x.events.canManage) && (
                <Button
                  className="w-full"
                  styling="secondary"
                  type="link"
                  onClick={() => handleClickFollowUpAction(SurveyFollowUpAction.CREATE_EVENT)}
                  href={routes.events.create({ slug })}
                >
                  {t("page.survey-details.follow-up-actions.button.create-event")}
                </Button>
              )}
              {hasPermission((x) => x.canCreatePosts) && (
                <Button
                  className="w-full"
                  styling="secondary"
                  type="link"
                  onClick={() => handleClickFollowUpAction(SurveyFollowUpAction.CREATE_MESSAGE)}
                  href={routes.messageFeed.adminCreatePost({ slug })}
                >
                  {t("page.survey-details.follow-up-actions.button.create-message")}
                </Button>
              )}
              {hasPermission((x) => x.surveys.canManage) && (
                <Button
                  className="w-full"
                  styling="secondary"
                  type="link"
                  onClick={() => handleClickFollowUpAction(SurveyFollowUpAction.CREATE_SURVEY)}
                  href={routes.surveys.new({ slug })}
                >
                  {t("page.survey-details.follow-up-actions.button.create-survey")}
                </Button>
              )}
            </div>
            {!survey.followedUpAt && (
              <Button
                className="w-full"
                styling="secondary"
                onClick={() => handleClickFollowUpAction(SurveyFollowUpAction.IGNORE)}
              >
                {t("common.action.ignore")}
              </Button>
            )}
          </div>
        </div>
      </div>

      {hasAiResults && (
        <SurveyFollowUpTipsModal
          surveyId={survey.id}
          isOpened={isFollowUpModalOpened}
          onOpenChange={followUpModalHandler.set}
        />
      )}
      {!hasAiResults && (
        <SurveyFollowUpTipsModal_LEGACY
          surveyId={survey.id}
          isOpened={isFollowUpModalOpened}
          onOpenChange={followUpModalHandler.set}
        />
      )}
    </>
  );
}
